// Stale-bundle self-heal for the recurring iOS-Safari-PWA cache problem
// (#374/#378/#406, #1956): no service worker + no-cache index.html means a
// normal reload is fresh, but a long-lived tab / installed PWA resumed days
// later still runs its OLD bundle, so frontend fixes look "not shipped".
// Vite content-hashes the entry chunk (/assets/index-<hash>.js), so a new
// deploy changes that filename; on resume-from-background we compare the hash
// this tab runs to the one the server now serves and reload if they differ.
// Rides Vite's hashing — no build flag, endpoint, or cache change.

// Only reload after a real away-gap, never mid-interaction. Tunable per env
// (no-hardcoding); default preserves the original 60s behaviour.
const MIN_BACKGROUND_MS = Number(import.meta.env.VITE_VERSION_CHECK_BG_MS ?? 60_000);

// Bound the probe so flaky ICU WiFi can't leave a stalled fetch hanging (~300s
// browser default) and starving the very resume this targets; it retries next
// resume. AbortError is swallowed by the caller's .catch below.
const PROBE_TIMEOUT_MS = 5_000;

// The entry <script type="module"> Vite injects, e.g. `/assets/index-<hash>.js`.
// Absent on the dev server (it serves /src/main.tsx) → initVersionCheck no-ops.
function entrySrcFrom(html: string): string | null {
  const m = html.match(/src="(\/assets\/index-[A-Za-z0-9_-]+\.js)"/);
  return m ? m[1] : null;
}

function runningEntrySrc(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  const src = el?.getAttribute('src');
  return src ? new URL(src, window.location.origin).pathname : null;
}

async function deployedEntrySrc(): Promise<string | null> {
  const res = await fetch('/', {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
    signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
  });
  if (!res.ok) return null;
  return entrySrcFrom(await res.text());
}

// Wire the resume-time freshness check. Call once at startup.
export function initVersionCheck(): void {
  const running = runningEntrySrc();
  if (!running) return; // dev server or unrecognised markup → nothing to compare

  let hiddenAt = 0;
  let inFlight = false;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    // Became visible. Act only after a real background gap, and never stack
    // probes (a stalled prior fetch must not spawn a second → double reload).
    if (inFlight || hiddenAt === 0 || Date.now() - hiddenAt < MIN_BACKGROUND_MS) return;
    hiddenAt = 0;
    inFlight = true;

    void deployedEntrySrc()
      .then((deployed) => {
        if (deployed && deployed !== running) {
          window.location.reload(); // newer build is live; swap this stale tab to it
        }
      })
      .catch(() => {
        // Offline / timeout / transient — leave the tab and re-check next resume.
      })
      .finally(() => {
        inFlight = false;
      });
  });
}
